import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { formatNumber, getLayout, getPromotionMainColor, i18nIntl, parseFontName, isColorBright, isNotEmpty } from '../../lib/ep.js';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';

import ActionButton from '../builder/ActionButton.js';
import ActionLink from '../builder/ActionLink.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import ColorPicker from '../builder/ColorPicker.js';
import DisplayList, { openAction } from '../builder/DisplayList.js';
import MediaUploader from '../builder/MediaUploader.js';
import Message, { AlertMessage } from '../builder/Message.js';
import Model from '../builder/Model.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import Range from '../builder/Range.js';
import RichEditor from '../builder/RichEditor.js';
import Select from '../builder/Select.js';
import TextArea from '../builder/TextArea.js';
import ToggleButton from '../builder/ToggleButton.js';
import Tooltip from '../builder/Tooltip.js';
import BuilderMain from '../builder/BuilderMain.js';

import Layout from '../front/Layout.js';

import ScratchStage from '../../containers/stages/ScratchStage.js';

import { OddsLayer } from './RouletteBuilder.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';

const TextAreaPlaceHolder = injectIntl(transferPlaceholder(TextArea));

const ScratchField = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	useEffect(() => {
		props.preview(model.id);

		return () => {
			props.preview(false);
		}
	}, []);

	let has_code = false;
	const pt = props.prizes.find((pr) => pr.id == model.id);
	if(pt?.type && pt.type > 0) has_code = true;

	const main_color = model?.bg_color ?? getPromotionMainColor(props.promotion);
	const text_color = model?.text_color || ((isColorBright(main_color)) ? '#000000' : '#FFFFFF');

	const display_text = (model.show_prize_name != undefined) ? model.show_prize_name : true;
	const display_code = (model.show_promo_code != undefined) ? model.show_promo_code : true;

	return (
		<div style={{paddingTop:'10px'}}>
			<AutoPostImageUploader
				auto_url={`/promotions/auto_img/${props.promotion_id}`}
				auto_type='stages'
				change={change}
				changeMulti={props.changeMulti}
				display_custom_dragging_instructions="Upload the prize image"
				image={model.full_scratch_image}
				name="scratch_image"
				small_input={true}
				_class={{first:true}}
				_size={{width:600, height:600}}
			>Prize image</AutoPostImageUploader>

			<Model model={model} change={change}>
				<ColorPicker _class={{size:60}} _hide_code={true} name="bg_color" color={main_color}>Background color</ColorPicker>

				<Checkbox name="show_prize_name" value={display_text}>Show prize text</Checkbox>

				{(display_text) && (
					<TextAreaPlaceHolder name="title" placeholder={(pt != undefined && pt.title) || i18n("No prize")}>Prize text</TextAreaPlaceHolder>
				)}

				{(has_code) && (
					<Checkbox name="show_promo_code" value={display_code}>Show promotional code</Checkbox>
				)}

				{(display_text || (has_code && display_code)) && (
					<ColorPicker _class={{size:60}} _hide_code={true} name="text_color" color={text_color}>Text color</ColorPicker>
				)}

				{(display_text || (has_code && display_code)) && (
					<Range min={1} max={5} type="fontsize" name="font_size" value={(model.font_size != undefined) ? model.font_size : 3} step={1} _class={{size:50}}>Font size</Range>
				)}
			</Model>
		</div>
	)
}

export const PrizeSubtitle = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const prize_type = props.prizes.find((p) => ( p.id == props.model.id ));
	if(prize_type != undefined && prize_type.assignation == '4') {
		// instant win:
		return (
			<span>{i18n("By Instant Win")}</span>
		)
	}
	if(props.stage.roulette_odds == undefined) return null;
	let odds = 0;
	if (props.model.id == 'prize') {
		odds = 1000 - props.stage.roulette_odds[0];
	}
	else if (props.stage.roulette_odds[props.model.id] != undefined) {
		odds = props.stage.roulette_odds[props.model.id];
	}
	const total = 1000;
	return (
		<FormattedMessage id="Odds: {odds}/{max} ({percent})%" values={{odds:odds, max:total, percent:formatNumber(100 * odds / total, 2)}} />
	)
}

const ScratchCover = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const saveStage = (cb) => {
		// TO DO
		/*let modified;
		change('scratch', modified);*/
		props.saveStage(cb);
	};

	const video_url = (stage.main_video) ? stage.main_video.url : null;

	return (
		<>
			<BuilderSidebar onSave={saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props.nav_sections || []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Message
					_class={{other:'in_top'}}
				>
					<p><strong>{i18n("Screen 1")}</strong></p>

					<p>{i18n("This is the cover image that users scratch to find out if they've won a prize.")}</p>
				</Message>

				<article>
					<Model model={stage} change={change}>
						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							display_custom_dragging_instructions="Upload the cover image"
							image={stage.full_scratch_cover}
							name='scratch_cover'
							small_input={true}
							_class={{first:true}}
							_size={{width:600, height:600}}
						>
							Cover image
						</AutoPostImageUploader>

						<MediaUploader imageField="main_image" videoField="main_video" image={stage.full_main_image} video={video_url}>Upload media</MediaUploader>

						<RichEditor name="main_description" has_emojis={true}>Introduction message</RichEditor>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<ScratchStage
						stage={{ Stage:stage }}
						is_builder={true}
						only_cover={true}
						prizes={props.prizes}
						promotion={{Promotion:props.promotion}}
					/>
				</Layout>
			</BuilderCanvas>
		</>
	)
}

const ScratchPrizes = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion ?? {};

	const prizeIsAvailable = (p) => {
		if (!['4','5'].includes(p.assignation) )return false;
		if (p.stage_id == null) return true;
		if (p.stage_id == stage.id) return true;
		return false;
	}

	const generateScratch = () => {
		if(stage.scratch) {
			let present = stage.scratch.map((p) => p.id);

			const current_prizes = props.prizes.filter(prizeIsAvailable).map(p=>p.id);
			stage.scratch = stage.scratch.filter(p=>{
				if (p.id == 0) return true;
				if (!current_prizes.includes(p.id))return false;
				return true;
			})

			if(!present.includes("0") && !present.includes(0)) {
				present.push("0");
				stage.scratch = [...stage.scratch,{id:0,type:'scratch_prize' }];
			}
			
			stage.scratch = stage.scratch.filter((p) => present.includes(p.id));

			props.prizes.filter(prizeIsAvailable).map((p) => {
				if(present.includes(p.id)) return;
				stage.scratch.unshift({id:p.id,type:'scratch_prize' })
			});

			return stage.scratch
		};

		const prizes_in_scratch = [...props.prizes.filter(prizeIsAvailable).map((p) => ({ id:p.id, type:'scratch_prize' })), { id:0, type:'scratch_prize' }];
		return prizes_in_scratch;
	};
	
	const [preview, setPreview] = useState(false);
	const [editing, setEditing] = useState(true);
	const [ghosting, setGhosting] = useState(false);
	const [show_grid, setShowGrid] = useState(true);
	const [odds_layer, setOddsLayer] = useState(false);
	const [typed_prizes, setTypedPrizes] = useState(generateScratch);
	const area = useRef();

	const dynamic_fields = [{
		type:'scratch_prize',
		label:false,
		title:(f) => {
			if(f.id == 0) return i18n("No prize");
			const p = props.prizes.find((p) => p.id == f.id);
			const title_parts = [];
			if(p.title != undefined) title_parts.push(p.title);
			if(isNotEmpty(p, 'reference')) title_parts.push(`(${p.reference})`);
			if(title_parts.length == 0) return "";
			return title_parts.join(" ");
		},
	}];

	const saveStage = useCallback((cb) => {
		if(editing != 'test') {
			change('scratch',typed_prizes);
			props.saveStage(cb);
			return;
		}
		let modified;
		setTypedPrizes((prizes) => {
			modified = prizes.map((pr) => {
				if(pr.id != current_prize.id) return pr;
				return { ...pr, area:area.current };
			})
			return modified;
		})

		change('scratch', modified);
		props.saveStage(cb);
		
	}, [typed_prizes, editing]);

	const updateCurrentArea = (area) => {
		if (current_prize.id === false) return;
		setTypedPrizes((prizes) => {
			return prizes.map((pr) => {
				if(pr.id != current_prize.id) return pr;
				if (area == false) return {...pr}
				return { ...pr, area };
			});
		});
	};

	const current_prize = typed_prizes.find((p) => p.id == preview);
	const layout_props = getLayout(promotion);
	const font_families = [
		{ k:'main', v:`${i18n("Main")} (${parseFontName(layout_props.text.main_font)})` },
		{ k:'secondary', v:`${i18n("***Secondary")} (${parseFontName(layout_props.text.secondary_font)})` },
		{ k:'generic', v:`${i18n("***Generic")} (${parseFontName("Arial")})` },
	];

	const instants = props.instantStore.getState().instant.instants;

	let instantCount = {};

	instants.map((instant) => {
		instantCount[instant.prize_type] = (instantCount[instant.prize_type] == undefined) ? 1 : instantCount[instant.prize_type] + 1;
	})

	const actions = [
		{
			display:(foo_props) => {
				
				const prize = props.prizes.find((p) => ( p.id == foo_props.field.id ));
				if (!prize) return false;
				switch (prize.assignation) {
					case '5':
						return (stage.roulette_odds != undefined && (stage.roulette_odds[foo_props.field.id] == undefined || stage.roulette_odds[foo_props.field.id] == 0))
					case '4':
						return (instantCount[prize.id] == undefined || instantCount[prize.id] < prize.qty)
				}	
				return false;
			},
			squared:true,
			color:'red',
			_class:(props) => ({icon:'knk-alert'}),
			tooltip:(foo_props) => {
				const prize = props.prizes.find((p) => ( p.id == foo_props.field.id ));

				let tooltip_text = false;
				if(prize != undefined) {
					switch(prize.assignation) {
						case '5':
							// by probability:
							tooltip_text = "This prize has 0 odds";
							break;

						case '4':
							// instant win:
							tooltip_text = "The winning moments have not been scheduled for all units of this prize. Go to the Instant Win tool and assign a moment to each unit.";
							break;
					}

					return (
						<Tooltip with_icon={false} position="top">{tooltip_text}</Tooltip>
					);
				}

				return null;
			},
		},
		openAction
	];

	const userInfo = { c:'123456789' };

	useEffect(() => {
		setEditing(true);
	}, [preview]);

	const ViewOptions = (
		<ButtonCollection _class={{other:'gui_flex_space_between'}}>
			<ButtonCollection>
				{(editing) ? (
					<>
						<ToggleButton active={show_grid} _class={{color:((show_grid) ? 'green' : 'gray'), icon:'knk-hashtag', icon_off:'knk-hashtag'}} onClick={() => setShowGrid(!show_grid)}>Show guides</ToggleButton>

						<ToggleButton active={ghosting} _class={{color:((ghosting) ? 'green' : 'gray'), icon:'knk-layers', icon_off:'knk-layers'}} onClick={() => setGhosting(!ghosting)}>Show cover image</ToggleButton>
					</>
				) : (
					<>
						<ActionButton
							color='lightgray'
							icon='knk-hashtag'
							_class={{ other:'gui_remove_pointer_events' }}
						>Show guides</ActionButton>

						<ActionButton
							color='lightgray'
							icon='knk-layers'
							_class={{ other:'gui_remove_pointer_events' }}
						>Show cover image</ActionButton>
					</>
				)}
			</ButtonCollection>

			<ButtonCollection>
				<ToggleButton
					active={!editing}
					color={(editing) ? 'green' : 'gray'}
					icon='knk-visible'
					icon_off='knk-visible'
					onClick={() => setEditing(!editing)}
				>Test this prize</ToggleButton>
			</ButtonCollection>
		</ButtonCollection>
	);

	return (
		<>
			<BuilderSidebar onSave={saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props.nav_sections || []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Message
					_class={{other:'in_top'}}
				>
					<p><strong>{i18n("Screen 2")}</strong></p>

					<p>{i18n("This is the image of the prize or no prize that shows after the participants scratch the cover image.")}</p>

					<p>{i18n("The red square marks the area that participants have to scratch to see the prize/no prize. You can move and resize the square.")}</p>
				</Message>

				{(typed_prizes.length < 2) && (
					<AlertMessage
						title="No prizes"
						_class={{ other:'in_top' }}
					>
						<p>{i18n("There is no prize.")} <ActionLink _href="#prizes">{i18n("Create first prize")}</ActionLink></p>
					</AlertMessage>
				)}

				{(stage.roulette_odds == undefined) && (
					<AlertMessage
						title="No odds"
						_class={{ other:'in_top' }}
					>
						<p>{i18n("The probability of awarding prizes has not been defined.")} <ActionLink onClick={() => setOddsLayer(true)}>Define odds now</ActionLink></p>
					</AlertMessage>
				)}

				<article class="gui_not_in_top_huge">
					<DisplayList
						onChange={setTypedPrizes}
						field_types={dynamic_fields}
						fields={typed_prizes}
						actions={actions}
						section_title={i18n("Prizes")}
						title_action={<ActionLink onClick={() => setOddsLayer(true)}>Configure prize odds</ActionLink>}
						subtitle={<PrizeSubtitle stage={stage} prizes={props.prizes} intl={props.intl} />}
					>
						<ScratchField
							preview={setPreview}
							prizes={props.prizes}
							promotion_id={stage.promotion_id}
							promotion={props.promotion}
							intl={props.intl}
							is_editing={editing}
							toggleEditing={setEditing}
						/>
					</DisplayList>
					
					{(odds_layer) && (
						<OddsLayer
							close={() => setOddsLayer(false)}
							max_odds_summ={1000}
							stage={stage}
							has_no_prize_slices={true}
							has_prizes_iw={false}
							prizes={props.prizes}
							change={change}
							on_save={props.on_save}
						/>
					)}
				</article>

				<article class="gui_not_in_top_huge">
					<Model model={stage} change={change}>
						<Select
							name="font_family"
							values={font_families}
							_class={{ size:70 }}
						>Typography</Select>

						<Checkbox
							name="auto_transition"
						>Automatic transition to the final page after revealing the result</Checkbox>

						<Checkbox
							name="silence"
						>Disable sounds</Checkbox>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content" off_content={ViewOptions}>
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<ScratchStage
						stage={{ Stage:stage }}
						is_builder={true}
						is_editing={editing}
						is_ghosting={ghosting}
						show_grid={show_grid}
						prizes={props.prizes}
						given_prize={preview}
						updateArea={updateCurrentArea}
						scratch={current_prize}
						userInfo={userInfo}
						promotion={{Promotion:props.promotion}}
					/>
				</Layout>
			</BuilderCanvas>
		</>
	)
}

const ScratchBuilder = ({ base_route, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const basename = `/stages/${props.stage.id}/scratch`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:`${i18n("Screen 1")} - ${i18n("Cover")}`, link:`${basename}/cover`, icon:'knk-picture', autotranslate:false },
		{ label:`${i18n("Screen 2")} - ${i18n("Prizes")}`, link:`${basename}/scratch_prizes`, icon:'knk-present', autotranslate:false },
	];

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/cover">
						<ScratchCover
							change={props.change}
							intl={props.intl}
							nav_active={`/cover`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							prizes={props.prizes}
							saveStage={props.saveStage}
							winnerStore={props.winnerStore}
							stage={props.stage}
							on_save={props.on_save}
							stage_indicator={props.stage_indicator}
						/>
					</Route>

					<Route exact={true} path="/scratch_prizes">
						<ScratchPrizes
							change={props.change}
							intl={props.intl}
							nav_active={`/scratch_prizes`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							instantStore={props.instantStore}
							prizes={props.prizes}
							saveStage={props.saveStage}
							winnerStore={props.winnerStore}
							stage={props.stage}
							on_save={props.on_save}
							stage_indicator={props.stage_indicator}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(ScratchBuilder);